import React, { useEffect, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/app/hooks';
import { getCachedProfile, getMyProfile, changePassword, type Employee } from '@/lib/hrApi';
import { TwoFactorCard } from './TwoFactorCard';
import { SessionCard } from './SessionCard';
import { ConnectedAppsCard } from './ConnectedAppsCard';
import { InstallPanel } from '@/features/shared/components/install/InstallPanel';
import { NotificationPreferencesCard } from './NotificationPreferencesCard';
import { DesktopNotificationsPanel } from '@/features/shared/components/notifications/DesktopNotificationsPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { User, Lock, Globe, Check, ShieldCheck } from 'lucide-react';

export const AccountSettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const { user } = useAppSelector((state) => state.auth);
  const [profile, setProfile] = useState<Employee | null>(getCachedProfile());

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    getMyProfile().then(setProfile).catch(() => { /* keep cached */ });
  }, []);

  const passwordValid = (pw: string) =>
    pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /(\d|\W)/.test(pw);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) return;
    if (newPassword !== confirmPassword) {
      toast.error(t('settings:passwords_no_match', 'New passwords do not match'));
      return;
    }
    if (!passwordValid(newPassword)) {
      toast.error(t('settings:password_weak', 'Password needs 8+ chars with upper, lower and a number/symbol'));
      return;
    }
    setChangingPw(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success(t('settings:password_changed', 'Password changed successfully'));
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err: any) {
      const msg = getApiErrorMessage(err);
      toast.error(getApiErrorMessage(err, Array.isArray(msg) ? msg[0] : (msg || t('settings:password_change_failed', 'Could not change password'))));
    } finally {
      setChangingPw(false);
    }
  };

  const fullName = profile
    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
    : user?.name || '—';
  const roleLabel = user?.role ? user.role.replace(/[-_]/g, ' ') : 'Employee';

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-6">
        <h1 className="text-h1 text-charcoal">{t('settings:account_settings', 'Account Settings')}</h1>
        <p className="text-muted-foreground mt-1">{t('settings:account_subtitle', 'Manage your account, security and preferences')}</p>
      </div>

      <Tabs defaultValue="account" className="space-y-6">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="account">{t('settings:tab_account', 'Account')}</TabsTrigger>
          <TabsTrigger value="security">{t('settings:tab_security', 'Security')}</TabsTrigger>
          <TabsTrigger value="notifications">{t('settings:tab_notifications', 'Notifications')}</TabsTrigger>
          <TabsTrigger value="preferences">{t('settings:tab_preferences', 'Preferences')}</TabsTrigger>
        </TabsList>

        {/* ---- Account ---- */}
        <TabsContent value="account" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" /> {t('settings:account_info', 'Account Information')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('settings:full_name', 'Full Name')}</Label>
                  <Input value={fullName} readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings:email', 'Email')}</Label>
                  <Input value={profile?.email || user?.email || ''} readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings:employee_id', 'Employee ID')}</Label>
                  <Input value={profile?.employee_id || '—'} readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings:user_type', 'User Type')}</Label>
                  <Input value={roleLabel} readOnly className="bg-muted capitalize" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('settings:edit_profile_hint', 'To update personal details (phone, address, emergency contacts), go to your')}{' '}
                <Link to="../personal" className="text-orange-600 hover:underline font-medium">
                  {t('settings:profile', 'Profile')}
                </Link>.
              </p>
            </CardContent>
          </Card>
                {/* The permanent discovery path. It lives on Account because
            installing the app is not a security setting — it was under
            Security, where nobody looks for it, and read as missing.
            Renders nothing when already installed, or when the browser
            offers no route. */}
        <InstallPanel />

</TabsContent>

        {/* ---- Security ---- */}
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" /> {t('settings:change_password', 'Change Password')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="current-pw">{t('settings:current_password', 'Current Password')}</Label>
                  <Input id="current-pw" type="password" autoComplete="current-password"
                    value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-pw">{t('settings:new_password', 'New Password')}</Label>
                  <Input id="new-pw" type="password" autoComplete="new-password"
                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                  <p className="text-xs text-muted-foreground">
                    {t('settings:password_rules', 'At least 8 characters with uppercase, lowercase and a number or symbol.')}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-pw">{t('settings:confirm_password', 'Confirm New Password')}</Label>
                  <Input id="confirm-pw" type="password" autoComplete="new-password"
                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                </div>
                <Button type="submit" disabled={changingPw}>
                  {changingPw ? t('settings:updating', 'Updating…') : t('settings:update_password', 'Update Password')}
                </Button>
              </form>
            </CardContent>
          </Card>

          <SessionCard />

          <TwoFactorCard />

          <ConnectedAppsCard />

        </TabsContent>

        {/* ---- Notifications ---- */}
        <TabsContent value="notifications" className="space-y-6">
          {/* Per-channel preferences live in notification-service (the legacy
              localStorage-only toggles were removed — they never reached delivery). */}
          {/* Permission is asked from this panel's button and nowhere else.
              It renders nothing when the browser cannot do it, or when the
              deployment has no VAPID keys. */}
          <DesktopNotificationsPanel />
          <NotificationPreferencesCard />
        </TabsContent>

        {/* ---- Preferences ---- */}
        <TabsContent value="preferences" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" /> {t('settings:language', 'Language')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {[
                { code: 'en', flag: '🇬🇧', label: 'English' },
                { code: 'sw', flag: '🇹🇿', label: 'Kiswahili' },
              ].map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => { i18n.changeLanguage(lang.code); toast.success(t('common:saved', 'Saved')); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors text-left ${
                    i18n.language === lang.code ? 'border-orange-400 bg-orange-50 dark:bg-orange-500/[0.08]' : 'border-border hover:bg-muted'
                  }`}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <span className="font-medium">{lang.label}</span>
                  {i18n.language === lang.code && <Check className="ml-auto w-4 h-4 text-orange-600" />}
                </button>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AccountSettingsPage;
